import { DatePipe } from '@angular/common';
import { Injectable, LOCALE_ID, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject } from 'rxjs';
import { CONST_TIMEZONES } from '@fitness-force/constants';

@Injectable({
  providedIn: 'root',
})
export class TimeconversionService {
  private store = inject(Store);
  private locale = inject(LOCALE_ID);
  private datePipe = inject(DatePipe);

  languageCode = new BehaviorSubject('');
  format: string = 'yyyy-MM-ddTHH:mm:ss';

  private readonly datePatterns = new Map<string, string>([
    ['short', 'd/M/y, h:mm:ss a'],
    ['medium', 'MMM d, y, h:mm:ss a'],
    ['long', 'EEEE, LLLL d, y, h:mm:ss a'],
    ['dateOnly', 'MMM d, y'],
    ['full', 'EEEE, MMMM d, y, h:mm:ss a zzzz'],
    ['shortDate', 'M/d/yy'],
    ['mediumDate', 'MMM d, y'],
    ['longDate', 'MMMM d, y'],
    ['fullDate', 'EEEE, MMMM d, y'],
    ['shortTime', 'h:mm a'],
    ['mediumTime', 'h:mm:ss a'],
    ['longTime', 'h:mm:ss a z'],
    ['fullTime', 'h:mm:ss a zzzz'],
    ['atDate', 'dd MMM yyyy,hh:mm a'],
    ['usformat', 'M/d/yyyy'],
    ['expiryDate', 'M/d'],
  ]);

  formatDate(date: Date | string, format?: string): any {
    let pattern = this.datePatterns.get('dateOnly');
    if (format) {
      const formatPattern = this.datePatterns.get(format);
      if (formatPattern) pattern = formatPattern;
    }
    if (this.languageCode.value !== 'en') {
      const datePipe: DatePipe = new DatePipe('en');
      return datePipe.transform(date, pattern);
    } else {
      const datePipe: DatePipe = new DatePipe(this.languageCode.value);
      return datePipe.transform(date, pattern);
    }
  }

  // Convert UTC time to Local Time
  ConvertUTCToLocalDateTime(date: Date | string): Date {
    const localDate = new Date(date);
    const offset = new Date().getTimezoneOffset();
    localDate.setMinutes(localDate.getMinutes() - offset);
    return localDate;
  }

  // Convert Local Time To UTC
  ConvertLocalDateTimeToUTC(date: Date | string): string {
    // return this.datePipe.transform(new Date(date).toISOString(), this.format,"UTC","en-US")!.toString();
    return new Date(date).toISOString();
  }

  formatISOStringWithoutMilliseconds(isoString: string): string {
    // Remove milliseconds if present (format: .123Z or .123)
    // Also remove trailing Z if present
    let result = isoString;
    // Remove milliseconds pattern: .123Z or .123
    result = result.replace(/\.\d{1,3}Z?$/, '');
    // Remove trailing Z if still present
    result = result.replace(/Z$/, '');
    return result;
  }

  // Get converted UTC datetime by date and time
  GetUTCDateTimeByDateAndTime(
    date: Date | string,
    time: Date | string
  ): string {
    time = new Date(time);
    date = new Date(date);
    const followupDateTime = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      time.getHours(),
      time.getMinutes(),
      time.getSeconds()
    ).toISOString();
    return followupDateTime;
  }

  GetUTCTimeFromDate(date: Date | string) {
    const utcDate = new Date(date).toISOString();
    return this.datePipe.transform(utcDate, 'H:mm', 'UTC')?.toString() || '';
  }

  // function to get end date with its end time
  getEndOfDay(date: Date): Date {
    const endDate = new Date(date); // Create a new Date object based on the input date
    endDate.setHours(23); // Set hours to 23 (11 PM)
    endDate.setMinutes(59); // Set minutes to 59
    endDate.setSeconds(59);
    return endDate;
  }

  GetUTCDateTimeWithFormat(date: Date | string, format?: string) {
    if (format)
      return this.datePipe.transform(date, format, 'UTC')?.toString() || '';
    else
      return (
        this.datePipe.transform(date, this.format, 'UTC')?.toString() || ''
      );
  }

  // Convert Local Time To UTC Date Object
  ConvertLocalDateTimeToUTCInDateObject(date: Date): Date {
    const year = date.getUTCFullYear();
    const month = date.getUTCMonth();

    const day = date.getUTCDate();
    const hours = date.getUTCHours();
    const minutes = date.getUTCMinutes();
    const seconds = date.getUTCSeconds();
    return new Date(year, month, day, hours, minutes, seconds);
  }

  // Get start of day in UTC with seconds precision (00:00:00)
  // Converts local midnight to UTC timestamp
  getStartOfDayUTC(date: Date | string): string {
    const inputDate = new Date(date);
    // Get LOCAL date components (not UTC components)
    const year = inputDate.getFullYear();
    const month = inputDate.getMonth();
    const day = inputDate.getDate();
    // Create new date at start of LOCAL day (00:00:00 in local timezone)
    const localStartOfDay = new Date(year, month, day, 0, 0, 0, 0);
    // Convert to UTC by using toISOString
    return localStartOfDay.toISOString().slice(0, 19) + 'Z';
  }

  // Get end of day in UTC with seconds precision (23:59:59)
  // Converts local end-of-day to UTC timestamp
  getEndOfDayUTC(date: Date | string): string {
    const inputDate = new Date(date);
    // Get LOCAL date components (not UTC components)
    const year = inputDate.getFullYear();
    const month = inputDate.getMonth();
    const day = inputDate.getDate();
    // Create new date at end of LOCAL day (23:59:59 in local timezone)
    const localEndOfDay = new Date(year, month, day, 23, 59, 59, 0);
    // Convert to UTC by using toISOString
    return localEndOfDay.toISOString().slice(0, 19) + 'Z';
  }

  // Convert date to UTC with seconds precision (no milliseconds)
  convertToUTCWithSeconds(date: Date | string): string {
    const utcDate = new Date(date);
    // Format with seconds precision and include T and Z
    return utcDate.toISOString().slice(0, 19) + 'Z';
  }

  convertTenantOperationalDateFromUtcToTenantTimezone(
    date: Date | string,
    tenantTimezone: string
  ): string {
    if (!date || !tenantTimezone) {
      return '-';
    }

    // Find the timezone info from CONST_TIMEZONES
    const timezones = CONST_TIMEZONES;
    const timezoneInfo = timezones.find((tz) => tz.Id === tenantTimezone);

    if (!timezoneInfo) {
      // Fallback to original date if timezone not found
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
      });
    }

    // Convert the date to the tenant's timezone
    const convertedDate = this.convertDatetimeToTimezone(
      new Date(date),
      timezoneInfo.BaseUtcOffset
    );

    // Format the date using MMM d, y, h:mm:ss a format
    return convertedDate.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
  }

  /**
   * Convert datetime to specific timezone using UTC offset
   * @param datetime - The date to convert
   * @param offsetString - UTC offset string like "+04:00" or "-05:00"
   * @returns Converted date
   */
  convertDatetimeToTimezone(datetime: Date, offsetString: string): Date {
    // Determine the sign (positive if no sign)
    let sign = 1;
    if (offsetString.startsWith('-')) {
      sign = -1;
      offsetString = offsetString.substring(1); // Remove the sign for parsing
    } else if (offsetString.startsWith('+')) {
      offsetString = offsetString.substring(1); // Remove the + sign for parsing
    }

    // Split the offset into hours, minutes, and seconds
    const [hoursStr, minutesStr, secondsStr] = offsetString.split(':');

    const hours = parseInt(hoursStr, 10) || 0;
    const minutes = parseInt(minutesStr, 10) || 0;
    const seconds = parseInt(secondsStr, 10) || 0;

    const totalOffsetMilliseconds =
      sign * ((hours * 3600 + minutes * 60 + seconds) * 1000);

    // Apply the timezone offset directly to the datetime (don't convert to UTC first)
    const targetTime = datetime.getTime() + totalOffsetMilliseconds;
    return new Date(targetTime);
  }
}
